// Watch it build: the pure side. Which tool calls form a friendly step group, how a group is named (DES1's deterministic
// priority), the one App card of a thread, the live status line and the optional next-step suggestions. Presentation only:
// it reads the message's own parts and never changes execution, approval or any result.
import { appPreviewTarget, liveAppPreviewTarget } from '@/workspace/apps/preview/tool-output';
import { NEW_APP_CANVAS } from '@/workspace/apps/new-app';
import type { CanvasTarget } from '@/workspace/types';
import { formatCount } from '@/i18n/format';
import { formatCopy } from '@/i18n/ns';
import type { BuildWatchCopyKey } from '@/i18n/ns/build-watch';
import { toolOutcome } from './tool-presentation';

export type BuildCopy = Readonly<Record<BuildWatchCopyKey, string>>;
export type StepKind =
  | 'write'
  | 'build'
  | 'preview'
  | 'screenshot'
  | 'url'
  | 'read'
  | 'run'
  | 'other';
type Part = {
  type?: string;
  text?: string;
  toolCallId?: string;
  toolName?: string;
  argsText?: string;
  args?: unknown;
  result?: unknown;
  status?: { type: string; reason?: string };
  isError?: boolean;
};

const tokens = (name: string) =>
  new Set(
    name
      .toLowerCase()
      .split(/[^a-z0-9]+/u)
      .filter(Boolean),
  );
const has = (set: Set<string>, ...words: string[]) => words.some((word) => set.has(word));

/** An App turn's tools are the sandbox tools of the App kit (`app_*`); every other turn keeps today's activity. */
export const isAppTool = (toolName: string | undefined) =>
  typeof toolName === 'string' && tokens(toolName).has('app');

/** What a tool call is, from its own name (product identifiers, not model text). */
export function stepKind(toolName: string): StepKind {
  const t = tokens(toolName);
  if (has(t, 'screenshot')) return 'screenshot';
  if (t.has('url')) return 'url';
  if (t.has('preview')) return 'preview';
  if (has(t, 'build', 'bundle', 'compile')) return 'build';
  if (has(t, 'write', 'edit', 'create', 'patch', 'apply', 'replace')) return 'write';
  if (has(t, 'run', 'exec', 'shell', 'bash', 'command')) return 'run';
  if (has(t, 'read', 'list', 'ls', 'glob', 'grep', 'search', 'find', 'tree', 'get')) return 'read';
  return 'other';
}

/** One run of consecutive tool calls (between two pieces of narration) as the thread groups them. */
export type StepGroup = { indices: number[] };

function domainOf(part: Part): string | null {
  const raw = (() => {
    try {
      const args = part.args ?? (part.argsText ? JSON.parse(part.argsText) : null);
      return args && typeof args === 'object' ? (args as { url?: unknown }).url : null;
    } catch {
      return null;
    }
  })();
  if (typeof raw !== 'string') return null;
  try {
    return new URL(raw).hostname.replace(/^www\./u, '') || null;
  } catch {
    return null;
  }
}

/** A valid finished preview or explicit saved-App open in this conversation. */
export function previewOf(part: Part | undefined, threadId: string | undefined) {
  if (!part || part.type !== 'tool-call' || !threadId) return null;
  const target = appPreviewTarget(part.toolName ?? '', part.result, threadId);
  return target && target.kind === 'app-preview' ? target : null;
}

export type GroupLabel = { label: string; steps: number; failed: number; running: boolean };

/**
 * DES1's deterministic label, in priority order: a write or build ("Built the App", "Updated the App" once a preview
 * already exists), a screenshot, a URL read ("Looked at {domain}"), reads/lists only ("Explored the project").
 */
export function groupLabel(
  parts: readonly Part[],
  copy: BuildCopy,
  previewBefore: boolean,
  appKind: 'app' | 'deck' | null = null,
): GroupLabel {
  const calls = parts.filter((part) => part.type === 'tool-call');
  const kinds = calls.map((call) => stepKind(call.toolName ?? ''));
  const failed = calls.filter(
    (call) => toolOutcome({ ...call, status: call.status as never }) === 'error',
  ).length;
  const runningCall = calls.find((call) => call.status?.type === 'running');
  const running = Boolean(runningCall);
  let label: string;
  if (running && runningCall) label = `${stepVerb(stepKind(runningCall.toolName ?? ''), copy)}…`;
  else if (kinds.some((kind) => ['write', 'build', 'preview'].includes(kind)))
    label = builtLabel(copy, previewBefore, appKind, kinds);
  else if (kinds.includes('screenshot')) label = copy.groupChecked;
  else if (kinds.includes('url')) {
    const domain = domainOf(calls[kinds.indexOf('url')]);
    label = domain ? formatCopy(copy.groupLookedAt, { domain }) : copy.groupExplored;
  } else if (kinds.every((kind) => kind === 'read')) label = copy.groupExplored;
  else label = stepVerb(kinds.find((kind) => kind !== 'other') ?? 'other', copy);
  return { label, steps: calls.length, failed, running };
}

/** "Built the {App|Deck}" only for a kind the preview itself reported; an unknown kind names the step, not the thing. */
function builtLabel(
  copy: BuildCopy,
  updated: boolean,
  appKind: 'app' | 'deck' | null,
  kinds: StepKind[],
): string {
  if (appKind === 'deck') return updated ? copy.groupUpdatedDeck : copy.groupBuiltDeck;
  if (appKind === 'app') return updated ? copy.groupUpdated : copy.groupBuilt;
  return stepVerb(
    kinds.find((kind) => ['build', 'write', 'preview'].includes(kind)) ?? 'other',
    copy,
  );
}

/** The present-tense verb (no ellipsis; callers add it) of a running step. */
export function stepVerb(kind: StepKind, copy: BuildCopy): string {
  switch (kind) {
    case 'write':
      return copy.stepWriting;
    case 'build':
      return copy.stepBuilding;
    case 'preview':
      return copy.stepOpeningPreview;
    case 'screenshot':
      return copy.stepCheckingLook;
    case 'run':
      return copy.stepRunning;
    case 'read':
      return copy.stepReading;
    default:
      return copy.stepListing;
  }
}

/** " · 3 steps" / " · 1 step didn’t work" suffixes, localized through the shared plural nouns. */
export function countSuffix(label: GroupLabel, copy: BuildCopy, locale: string): string {
  const out: string[] = [];
  if (label.steps > 1 && !label.running) out.push(formatCount(copy.stepsNoun, label.steps, locale));
  if (label.failed > 0) out.push(formatCount(copy.failedStepsNoun, label.failed, locale));
  return out.map((text) => ` · ${text}`).join('');
}

export type ThreadMessageLike = {
  id: string;
  role?: string;
  content?: readonly Part[];
  parts?: readonly Part[];
};
const partsOf = (message: ThreadMessageLike) => message.parts ?? message.content ?? [];

/** The one App card of a thread: the first finished preview in transcript order, and the latest preview's target. */
export function threadApp(messages: readonly ThreadMessageLike[], threadId: string | undefined) {
  let first: { messageId: string; toolCallId: string } | null = null;
  let latest: CanvasTarget | null = null;
  let previews = 0;
  let latestMessageId: string | null = null;
  for (const message of messages) {
    for (const part of partsOf(message)) {
      const target = previewOf(part, threadId);
      if (!target) continue;
      previews += 1;
      first ??= { messageId: message.id, toolCallId: part.toolCallId ?? '' };
      latest = target;
      latestMessageId = message.id;
    }
  }
  const appKind = latest && latest.kind === 'app-preview' ? (latest.appKind ?? null) : null;
  return { first, latest, previews, latestMessageId, appKind };
}

/**
 * The canvas that shows this thread's App (or Deck) being built: the newest finished preview, else the newest live
 * build, else the thread's own waiting App panel (which shows the build as it happens).
 */
export function buildCanvasTarget(
  messages: readonly ThreadMessageLike[],
  threadId: string | undefined,
): CanvasTarget {
  let target: CanvasTarget | null = null;
  if (threadId)
    for (const message of messages)
      for (const part of partsOf(message)) {
        if (part.type !== 'tool-call') continue;
        const found =
          appPreviewTarget(part.toolName ?? '', part.result, threadId) ??
          liveAppPreviewTarget(part.toolName, part.result, threadId);
        if (found) target = found;
      }
  return target ?? NEW_APP_CANVAS;
}

/** True when the open canvas already shows an App build, so a Show control would only repeat it. */
export const showsBuild = (canvas: CanvasTarget | null | undefined) =>
  canvas?.kind === 'app-preview';

/** The live status line's text: the latest narration line, else the running step's verb with an ellipsis. */
export function statusText(parts: readonly Part[], copy: BuildCopy): string | null {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part.type === 'tool-call' && part.status?.type === 'running')
      return `${stepVerb(stepKind(part.toolName ?? ''), copy)}…`;
    if (part.type === 'text' && part.text?.trim()) {
      const lines = part.text.split('\n').map((line) => line.trim());
      return lines.filter(Boolean).at(-1) ?? null;
    }
  }
  return null;
}

/** Optional next-step phrases of the latest finished preview (appPreview.suggestions): strings only, trimmed, <= 200 chars, at most six shown, never required. */
export function previewSuggestions(parts: readonly Part[], threadId: string | undefined): string[] {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (!previewOf(part, threadId)) continue;
    const raw = (part.result as { appPreview?: { suggestions?: unknown } } | undefined)?.appPreview
      ?.suggestions;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter((item) => item.length > 0 && item.length <= 200)
      .slice(0, 6);
  }
  return [];
}
